// calendarEventColors.js

import { useCallback } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getFieldCalendarColor, resolveThemePaletteColor } from 'utilities/CalendarFieldColor';
import { getReadableForegroundColor } from 'utilities/Colors';

// Experiment: Appdeck mark–inspired event chip colors (stable per event via hash).
export const ANYBASE_EVENT_PALETTE = [
  '#FF2D55', // hot pink
  '#FF5E3A', // coral
  //'#FF9500', // orange
  //'#FFCC00', // yellow
  //'#4CD964', // neon green
  '#5AC8FA', // sky blue
  '#007AFF', // royal blue
  '#5856D6', // indigo
  '#AF52DE', // purple
];

function hashEventKey(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * resolveAppdeckEventColors
 *
 * Picks a stable Appdeck-palette background/foreground pair for a calendar event.
 *
 * @param {object} item - Calendar event item.
 * @param {number} [index=0] - Fallback index when the event has no stable identity.
 *
 * @returns {{ background: string, foreground: string }}
 */
export function resolveAppdeckEventColors(item, index = 0) {
  const key = [
    item?.node?.key,
    item?.field?.name,
    item?.occurrenceDateKey,
    item?.date?.getTime?.(),
    index,
  ].filter((part) => part != null && part !== '').join('|') || String(index);

  const background = ANYBASE_EVENT_PALETTE[hashEventKey(key) % ANYBASE_EVENT_PALETTE.length];
  return {
    background,
    foreground: getReadableForegroundColor(background),
  };
}

/**
 * resolveCalendarFieldColorToken
 *
 * Resolves the calendar color token for an event's temporal field. Base calendars
 * build events from denormalized rows that carry only field key/label/type, so the
 * live schema is consulted when the event's own field definition has no color.
 *
 * @param {object} item - Calendar event item.
 * @param {function} getType - Node type lookup from NodeTypesContext.
 *
 * @returns {string} A palette token id, or an empty string when the field has none.
 */
export function resolveCalendarFieldColorToken(item, getType) {
  const directToken = getFieldCalendarColor(item?.field);
  if (directToken) return directToken;

  const typeKey = item?.node?.type;
  const fieldName = item?.field?.name;
  if (!typeKey || !fieldName || typeof getType !== 'function') return '';

  const schemaFields = getType(typeKey)?.schema?.fields;
  if (!Array.isArray(schemaFields)) return '';

  return getFieldCalendarColor(schemaFields.find((field) => field?.name === fieldName));
}

/**
 * useCalendarEventColors
 *
 * Provides a resolver that maps a calendar event to its background and foreground
 * colors. The field stores a named theme token, so the active theme mode decides the
 * concrete shade. Falls back to caller-supplied theme colors when the field is uncolored.
 *
 * @returns {function} Resolver accepting (item, fallbackBackground, fallbackForeground).
 */
export default function useCalendarEventColors() {
  const { getType } = useNodeTypes();
  const { theme } = useTheme();

  return useCallback((item, fallbackBackground, fallbackForeground) => {
    const tokenId = resolveCalendarFieldColorToken(item, getType);
    const background = resolveThemePaletteColor(theme, tokenId);

    if (!background) {
      return { background: fallbackBackground, foreground: fallbackForeground };
    }

    return {
      background,
      foreground: getReadableForegroundColor(background),
    };
  }, [getType, theme]);
}
